import React, { useState } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Select,
  DatePicker,
  Button,
  Tabs,
  Space,
  Tag,
  Typography,
  Progress,
  Tooltip,
  Radio,
  Divider,
  List,
  Alert,
} from 'antd';
import {
  UserOutlined,
  RiseOutlined,
  FallOutlined,
  DollarOutlined,
  ShoppingOutlined,
  CalendarOutlined,
  ReloadOutlined,
  ExportOutlined,
  TeamOutlined,
  InfoCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { Column, Bar, Pie, DualAxes, Line } from '@ant-design/plots';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

// 会员消费转化数据接口
interface MemberConversion {
  id: string;
  name: string;
  level: string;
  bindTime: string;
  butler: string;
  beforeBinding: {
    totalSpent: number;
    orderCount: number;
    averageOrderValue: number;
    categories: string[];
  };
  afterBinding: {
    totalSpent: number;
    orderCount: number;
    averageOrderValue: number;
    categories: string[];
  };
  conversionRate: number;
  spendingIncrease: number;
  newCategories: string[];
}

// 品类数据接口
interface CategoryData {
  category: string;
  value: number;
  type: string;
}

// 月度转化率数据接口
interface MonthlyConversion {
  month: string;
  withButler: number;
  withoutButler: number;
}

// 会员等级转化率数据接口
interface LevelConversion {
  level: string;
  withButler: number;
  withoutButler: number;
}

// 营销活动转化数据接口
interface MarketingConversion {
  campaign: string;
  conversionRate: number;
  orderCount: number;
  revenue: number;
  butler: string;
}

// 模拟会员消费转化数据
const mockMemberConversions: MemberConversion[] = [
  {
    id: 'M001',
    name: '张三',
    level: '黑钻卡',
    bindTime: '2023-01-15',
    butler: '李管家',
    beforeBinding: {
      totalSpent: 15200,
      orderCount: 8,
      averageOrderValue: 1900,
      categories: ['住宿', '餐饮']
    },
    afterBinding: {
      totalSpent: 28500,
      orderCount: 12,
      averageOrderValue: 2375,
      categories: ['住宿', '餐饮', 'SPA服务', '会议服务']
    },
    conversionRate: 87.5,
    spendingIncrease: 87.5,
    newCategories: ['SPA服务', '会议服务']
  },
  {
    id: 'M002',
    name: '李四',
    level: '铂金卡',
    bindTime: '2023-02-20',
    butler: '王管家',
    beforeBinding: {
      totalSpent: 9800,
      orderCount: 6,
      averageOrderValue: 1633,
      categories: ['住宿', '餐饮']
    },
    afterBinding: {
      totalSpent: 16800,
      orderCount: 9,
      averageOrderValue: 1867,
      categories: ['住宿', '餐饮', 'SPA服务']
    },
    conversionRate: 71.4,
    spendingIncrease: 71.4,
    newCategories: ['SPA服务']
  },
  {
    id: 'M003',
    name: '王五',
    level: '金卡',
    bindTime: '2023-03-10',
    butler: '张管家',
    beforeBinding: {
      totalSpent: 7500,
      orderCount: 5,
      averageOrderValue: 1500,
      categories: ['住宿']
    },
    afterBinding: {
      totalSpent: 12500,
      orderCount: 7,
      averageOrderValue: 1786,
      categories: ['住宿', '餐饮', '商品零售']
    },
    conversionRate: 66.7,
    spendingIncrease: 66.7,
    newCategories: ['餐饮', '商品零售']
  },
  {
    id: 'M004',
    name: '赵六',
    level: '银卡',
    bindTime: '2023-04-05',
    butler: '刘管家',
    beforeBinding: {
      totalSpent: 3200,
      orderCount: 3,
      averageOrderValue: 1067,
      categories: ['住宿']
    },
    afterBinding: {
      totalSpent: 6800,
      orderCount: 5,
      averageOrderValue: 1360,
      categories: ['住宿', '餐饮']
    },
    conversionRate: 112.5,
    spendingIncrease: 112.5,
    newCategories: ['餐饮']
  },
  {
    id: 'M005',
    name: '钱七',
    level: '普卡',
    bindTime: '2023-05-15',
    butler: '赵管家',
    beforeBinding: {
      totalSpent: 1800,
      orderCount: 2,
      averageOrderValue: 900,
      categories: ['住宿']
    },
    afterBinding: {
      totalSpent: 3200,
      orderCount: 3,
      averageOrderValue: 1067,
      categories: ['住宿', '餐饮']
    },
    conversionRate: 77.8,
    spendingIncrease: 77.8,
    newCategories: ['餐饮']
  }
];

// 模拟品类数据
const mockCategoryData: CategoryData[] = [
  { category: '住宿', value: 100, type: '绑定前' },
  { category: '餐饮', value: 60, type: '绑定前' },
  { category: 'SPA服务', value: 15, type: '绑定前' },
  { category: '会议服务', value: 10, type: '绑定前' },
  { category: '商品零售', value: 5, type: '绑定前' },
  { category: '住宿', value: 100, type: '绑定后' },
  { category: '餐饮', value: 85, type: '绑定后' },
  { category: 'SPA服务', value: 65, type: '绑定后' },
  { category: '会议服务', value: 35, type: '绑定后' },
  { category: '商品零售', value: 30, type: '绑定后' }
];

// 模拟月度转化率数据
const mockMonthlyConversion: MonthlyConversion[] = [
  { month: '2023-01', withButler: 62, withoutButler: 35 },
  { month: '2023-02', withButler: 68, withoutButler: 38 },
  { month: '2023-03', withButler: 75, withoutButler: 40 },
  { month: '2023-04', withButler: 82, withoutButler: 42 },
  { month: '2023-05', withButler: 88, withoutButler: 45 },
  { month: '2023-06', withButler: 92, withoutButler: 48 }
];

// 模拟会员等级转化率数据
const mockLevelConversion: LevelConversion[] = [
  { level: '黑钻卡', withButler: 95, withoutButler: 85 },
  { level: '铂金卡', withButler: 88, withoutButler: 75 },
  { level: '金卡', withButler: 78, withoutButler: 62 },
  { level: '银卡', withButler: 65, withoutButler: 48 },
  { level: '普卡', withButler: 52, withoutButler: 32 }
];

// 模拟营销活动转化数据
const mockMarketingConversion: MarketingConversion[] = [
  {
    campaign: '春季SPA特惠',
    conversionRate: 68,
    orderCount: 156,
    revenue: 248000,
    butler: '李管家'
  },
  {
    campaign: '周末美食节',
    conversionRate: 72,
    orderCount: 208,
    revenue: 187200,
    butler: '王管家'
  },
  {
    campaign: '夏日泳池派对',
    conversionRate: 65,
    orderCount: 142,
    revenue: 156000,
    butler: '张管家'
  },
  {
    campaign: '会员专享下午茶',
    conversionRate: 78,
    orderCount: 186,
    revenue: 83700,
    butler: '李管家'
  },
  {
    campaign: '生日特别礼遇',
    conversionRate: 88,
    orderCount: 95,
    revenue: 142500,
    butler: '王管家'
  }
];

const ConsumptionConversionAnalysis: React.FC = () => {
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);
  const [activeTab, setActiveTab] = useState('overall');
  const [memberLevel, setMemberLevel] = useState('all');
  const [selectedButlers, setSelectedButlers] = useState<string[]>([]);

  // 根据会员等级筛选数据
  const getFilteredMemberData = () => {
    let filtered = [...mockMemberConversions];
    
    if (memberLevel !== 'all') {
      filtered = filtered.filter(member => member.level === memberLevel);
    }
    
    if (selectedButlers.length > 0) {
      filtered = filtered.filter(member => selectedButlers.includes(member.butler));
    }
    
    return filtered;
  };

  // 表格列定义
  const columns: ColumnsType<MemberConversion> = [
    {
      title: '会员信息',
      key: 'info',
      fixed: 'left',
      width: 180,
      render: (_, record) => (
        <div>
          <Text strong>{record.name}</Text>
          <div>
            <Tag color={
              record.level === '黑钻卡' ? 'black' : 
              record.level === '铂金卡' ? 'purple' : 
              record.level === '金卡' ? 'gold' : 
              record.level === '银卡' ? 'default' : 'blue'
            }>
              {record.level}
            </Tag>
          </div>
          <div>
            <Text type="secondary">绑定时间: {record.bindTime}</Text>
          </div>
        </div>
      ),
    },
    {
      title: '绑定管家',
      dataIndex: 'butler',
      key: 'butler',
      width: 100,
    },
    {
      title: '绑定前消费',
      key: 'beforeBinding',
      width: 200,
      render: (_, record) => (
        <div>
          <div>总消费: ¥{record.beforeBinding.totalSpent.toLocaleString()}</div>
          <div>订单数: {record.beforeBinding.orderCount}单</div>
          <div>客单价: ¥{record.beforeBinding.averageOrderValue.toLocaleString()}</div>
        </div>
      ),
    },
    {
      title: '绑定后消费',
      key: 'afterBinding',
      width: 200,
      render: (_, record) => (
        <div>
          <div>总消费: ¥{record.afterBinding.totalSpent.toLocaleString()}</div>
          <div>订单数: {record.afterBinding.orderCount}单</div>
          <div>客单价: ¥{record.afterBinding.averageOrderValue.toLocaleString()}</div>
        </div>
      ),
    },
    {
      title: '消费增长',
      key: 'spendingIncrease',
      width: 150,
      sorter: (a, b) => a.spendingIncrease - b.spendingIncrease,
      render: (_, record) => (
        <div>
          <Text strong style={{ color: '#52c41a' }}>+{record.spendingIncrease}%</Text>
          <Progress 
            percent={record.spendingIncrease} 
            size="small" 
            strokeColor={record.spendingIncrease >= 80 ? '#52c41a' : record.spendingIncrease >= 50 ? '#faad14' : '#1890ff'} 
          />
        </div>
      ),
    },
    {
      title: '品类拓展',
      key: 'categoryExpansion',
      width: 200,
      render: (_, record) => (
        <div>
          <div>绑定前: {record.beforeBinding.categories.map(cat => <Tag key={cat}>{cat}</Tag>)}</div>
          <div>新增: {record.newCategories.map(cat => <Tag key={cat} color="green">{cat}</Tag>)}</div>
        </div>
      ),
    },
    {
      title: '预订转化率',
      key: 'conversionRate',
      width: 150,
      sorter: (a, b) => a.conversionRate - b.conversionRate,
      render: (_, record) => (
        <div>
          <Text strong>{record.conversionRate}%</Text>
          <Progress 
            percent={record.conversionRate} 
            size="small" 
            strokeColor={record.conversionRate >= 80 ? '#52c41a' : record.conversionRate >= 60 ? '#faad14' : '#1890ff'} 
          />
        </div>
      ),
    },
  ];

  // 配置品类对比图
  const configCategory = {
    data: mockCategoryData,
    xField: 'category',
    yField: 'value',
    seriesField: 'type',
    isGroup: true,
    columnStyle: {
      radius: [10, 10, 0, 0],
    },
    label: {
      position: 'middle',
    },
    legend: {
      position: 'top',
    },
  };

  // 配置月度转化率对比图
  const configMonthly = {
    data: [
      mockMonthlyConversion.map(item => ({
        month: item.month,
        value: item.withButler,
        type: '绑定管家会员'
      })),
      mockMonthlyConversion.map(item => ({
        month: item.month,
        value: item.withoutButler,
        type: '普通会员'
      }))
    ].flat(),
    xField: 'month',
    yField: 'value',
    seriesField: 'type',
    yAxis: {
      label: {
        formatter: (v: string) => `${v}%`,
      },
    },
    legend: {
      position: 'top',
    },
    point: {
      size: 5,
      shape: 'circle',
    },
    label: {
      style: {
        fill: '#aaa',
      },
    },
  };

  // 配置会员等级转化率对比图
  const configLevel = {
    data: [
      {
        name: '绑定管家会员',
        data: mockLevelConversion.map(item => ({
          x: item.level,
          y: item.withButler
        }))
      },
      {
        name: '普通会员',
        data: mockLevelConversion.map(item => ({
          x: item.level,
          y: item.withoutButler
        }))
      }
    ],
    xField: 'x',
    yField: 'y',
    seriesField: 'name',
    yAxis: {
      label: {
        formatter: (v: string) => `${v}%`,
      },
    },
    legend: {
      position: 'top',
    },
    meta: {
      y: {
        min: 0,
        max: 100,
      },
    },
  };

  // 获取整体转化数据统计
  const getOverallStats = () => {
    const filteredData = getFilteredMemberData();
    
    if (filteredData.length === 0) {
      return {
        avgSpendingIncrease: 0,
        avgConversionRate: 0,
        avgNewCategories: 0,
        avgOrderIncrease: 0
      };
    }
    
    return {
      avgSpendingIncrease: Math.round(
        filteredData.reduce((sum, item) => sum + item.spendingIncrease, 0) / filteredData.length
      ),
      avgConversionRate: Math.round(
        filteredData.reduce((sum, item) => sum + item.conversionRate, 0) / filteredData.length
      ),
      avgNewCategories: (
        filteredData.reduce((sum, item) => sum + item.newCategories.length, 0) / filteredData.length
      ).toFixed(1),
      avgOrderIncrease: Math.round(
        filteredData.reduce((sum, item) => 
          sum + ((item.afterBinding.orderCount - item.beforeBinding.orderCount) / item.beforeBinding.orderCount * 100), 0
        ) / filteredData.length
      )
    };
  };

  const stats = getOverallStats();

  // 获取可用的管家选项
  const getButlerOptions = () => {
    const butlers = Array.from(new Set(mockMemberConversions.map(item => item.butler)));
    return butlers.map(butler => ({ label: butler, value: butler }));
  };

  return (
    <div className="consumption-conversion-analysis">
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col>
            <Title level={4} style={{ margin: 0 }}>会员消费转化分析</Title>
          </Col>
          <Col flex="auto">
            <div style={{ float: 'right' }}>
              <Space>
                <Select
                  placeholder="会员等级"
                  defaultValue="all"
                  style={{ width: 120 }}
                  onChange={(value) => setMemberLevel(value)}
                  options={[
                    { value: 'all', label: '全部等级' },
                    { value: '黑钻卡', label: '黑钻卡' },
                    { value: '铂金卡', label: '铂金卡' },
                    { value: '金卡', label: '金卡' },
                    { value: '银卡', label: '银卡' },
                    { value: '普卡', label: '普卡' },
                  ]}
                />
                <Select
                  placeholder="选择管家"
                  mode="multiple"
                  allowClear
                  style={{ width: 200 }}
                  onChange={(value) => setSelectedButlers(value)}
                  options={getButlerOptions()}
                  maxTagCount={2}
                />
                <RangePicker onChange={(_, dateStrings) => setDateRange(dateStrings as [string, string] | null)} />
                <Button icon={<ReloadOutlined />}>刷新数据</Button>
                <Button type="primary" icon={<ExportOutlined />}>导出报告</Button>
              </Space>
            </div>
          </Col>
        </Row>
      </Card>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="平均消费增长率"
              value={stats.avgSpendingIncrease}
              suffix="%"
              valueStyle={{ color: '#3f8600' }}
              prefix={<RiseOutlined />}
            />
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">绑定管家会员比普通会员高 <Text type="success">+42%</Text></Text>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="平均预订转化率"
              value={stats.avgConversionRate}
              suffix="%"
              valueStyle={{ color: '#1890ff' }}
              prefix={<UserOutlined />}
            />
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">绑定管家会员比普通会员高 <Text type="success">+35%</Text></Text>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="平均新增品类数"
              value={stats.avgNewCategories}
              valueStyle={{ color: '#cf1322' }}
              prefix={<ShoppingOutlined />}
            />
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">绑定后平均拓展消费品类数</Text>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="平均订单增长率"
              value={stats.avgOrderIncrease}
              suffix="%"
              valueStyle={{ color: '#722ed1' }}
              prefix={<CalendarOutlined />}
            />
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">绑定管家会员比普通会员高 <Text type="success">+28%</Text></Text>
            </div>
          </Card>
        </Col>
      </Row>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="整体转化分析" key="overall">
          <Card>
            <Alert
              message="管家绑定效果显著"
              description="数据显示，绑定管家后会员的消费金额、频次和品类覆盖明显提升，平均消费增长超过75%。管家服务为酒店提供了显著的营收增长和客户忠诚度提升。"
              type="success"
              showIcon
              style={{ marginBottom: 16 }}
            />
            
            <Title level={5}>绑定管家会员消费转化明细</Title>
            <Table
              dataSource={getFilteredMemberData()}
              columns={columns}
              rowKey="id"
              scroll={{ x: 1200 }}
              pagination={{ pageSize: 5 }}
            />
          </Card>
        </TabPane>
        
        <TabPane tab="品类扩展分析" key="category">
          <Card>
            <Title level={5}>会员消费品类覆盖率对比 <Tooltip title="绑定管家后会员消费品类显著扩展，尤其是高价值品类如SPA服务和会议服务"><InfoCircleOutlined /></Tooltip></Title>
            {mockCategoryData && mockCategoryData.length > 0 && (
              <Column {...configCategory} height={400} />
            )}
            
            <Divider />
            
            <Row gutter={16}>
              <Col span={24}>
                <Card title="热门扩展品类分析" bordered={false}>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Title level={5}>绑定管家会员新增品类排行</Title>
                      <List
                        dataSource={[
                          { category: 'SPA服务', rate: 85, growth: '+68%' },
                          { category: '会议服务', rate: 65, growth: '+52%' },
                          { category: '商品零售', rate: 60, growth: '+45%' },
                          { category: '会员专享活动', rate: 55, growth: '+38%' },
                          { category: '尊享体验服务', rate: 48, growth: '+35%' },
                        ]}
                        renderItem={(item) => (
                          <List.Item>
                            <div style={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                              <div style={{ flex: 1 }}>{item.category}</div>
                              <div style={{ width: 200 }}>
                                <Progress percent={item.rate} size="small" />
                              </div>
                              <div style={{ width: 80, paddingLeft: 16 }}>
                                <Text type="success">{item.growth}</Text>
                              </div>
                            </div>
                          </List.Item>
                        )}
                      />
                    </Col>
                    <Col span={12}>
                      <Card title="消费品类扩展成效" bordered={false}>
                        <Paragraph>
                          <ul>
                            <li>绑定管家后，会员在SPA服务的消费比例提升了<Text strong>68%</Text></li>
                            <li>高端会员的品类扩展效果最显著，黑钻卡和铂金卡会员平均新增<Text strong>2.8</Text>个消费品类</li>
                            <li>会议服务成为增长最快的第二大品类，转化率达到<Text strong>65%</Text></li>
                            <li>绑定管家后的会员更倾向于尝试酒店新推出的特色服务，参与率高出普通会员<Text strong>42%</Text></li>
                            <li>管家主动推荐的商品零售品类，销售转化率达到<Text strong>60%</Text>，高于平均水平</li>
                          </ul>
                        </Paragraph>
                      </Card>
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>
          </Card>
        </TabPane>
        
        <TabPane tab="预订转化分析" key="conversion">
          <Card>
            <Radio.Group 
              onChange={(e) => console.log(e.target.value)} 
              defaultValue="monthly" 
              buttonStyle="solid"
              style={{ marginBottom: 16 }}
            >
              <Radio.Button value="monthly">月度转化率</Radio.Button>
              <Radio.Button value="member">会员等级分析</Radio.Button>
            </Radio.Group>
            
            <Row gutter={16}>
              <Col span={24}>
                <Card bordered={false}>
                  <Title level={5}>月度预订转化率对比</Title>
                  {configMonthly && configMonthly.data && configMonthly.data.length > 0 && (
                    <Line {...configMonthly} height={350} />
                  )}
                </Card>
              </Col>
            </Row>
            
            <Divider />
            
            <Row gutter={16}>
              <Col span={12}>
                <Card title="会员等级转化率对比" bordered={false}>
                  {configLevel && configLevel.data && configLevel.data.length > 0 && (
                    <DualAxes {...configLevel} height={350} />
                  )}
                </Card>
              </Col>
              <Col span={12}>
                <Card title="管家推荐营销活动转化" bordered={false}>
                  <Table
                    dataSource={mockMarketingConversion}
                    pagination={false}
                    columns={[
                      {
                        title: '活动名称',
                        dataIndex: 'campaign',
                        key: 'campaign',
                      },
                      {
                        title: '转化率',
                        dataIndex: 'conversionRate',
                        key: 'conversionRate',
                        render: (value) => `${value}%`,
                        sorter: (a, b) => a.conversionRate - b.conversionRate,
                      },
                      {
                        title: '订单数',
                        dataIndex: 'orderCount',
                        key: 'orderCount',
                        sorter: (a, b) => a.orderCount - b.orderCount,
                      },
                      {
                        title: '收益',
                        dataIndex: 'revenue',
                        key: 'revenue',
                        render: (value) => `¥${value.toLocaleString()}`,
                        sorter: (a, b) => a.revenue - b.revenue,
                      },
                      {
                        title: '主推管家',
                        dataIndex: 'butler',
                        key: 'butler',
                      },
                    ]}
                  />
                </Card>
              </Col>
            </Row>
          </Card>
        </TabPane>
      </Tabs>
    </div>
  );
};

export default ConsumptionConversionAnalysis; 